import { BaseEffect } from '../BaseEffect.js';

/**
 * Wave distortion effect implementation
 * Creates dynamic wave patterns with interference and animation
 */
export class WaveDistortionEffect extends BaseEffect {
    constructor(shaderManager) {
        const defaultParams = {
            amplitude: 0.02,     // Wave height (0-0.1)
            frequency: 5.0,      // Wave frequency (1-20)
            speed: 1.0,         // Wave animation speed
            direction: 0.0,      // Wave direction in degrees (0-360)
            turbulence: 0.5,    // Wave chaos/interference (0-1)
            ripples: 0.3,       // Circular ripple amount (0-1)
            refraction: 0.5,    // Light refraction amount
            chromatic: 0.2,     // Chromatic aberration
            waveCount: 3.0,     // Number of overlapping waves (1-5)
            animate: true,      // Whether to animate the waves
            pulseSpeed: 1.0,    // Speed of amplitude pulsing
            pulseAmount: 0.3    // Amount of amplitude pulsing
        };

        super('wave-distortion', shaderManager, defaultParams);
    }

    async init() {
        const vertexSource = `
            attribute vec2 a_position;
            attribute vec2 a_texCoord;
            varying vec2 v_texcoord;
            
            void main() {
                gl_Position = vec4(a_position, 0, 1);
                v_texcoord = a_texCoord;
            }
        `;

        const fragmentSource = `
            precision highp float;
            
            uniform sampler2D u_image;
            uniform float u_time;
            uniform vec2 u_resolution;
            uniform float u_amplitude;
            uniform float u_frequency;
            uniform float u_speed;
            uniform float u_direction;
            uniform float u_turbulence;
            uniform float u_ripples;
            uniform float u_refraction;
            uniform float u_chromatic;
            uniform float u_waveCount;
            
            varying vec2 v_texcoord;

            const float PI = 3.14159265359;
            const float TAU = PI * 2.0;

            // 2D Rotation matrix
            mat2 rotate2d(float angle) {
                float s = sin(angle);
                float c = cos(angle);
                return mat2(c, -s, s, c);
            }

            // Random function
            float random(vec2 st) {
                return fract(sin(dot(st.xy, vec2(12.9898,78.233))) * 43758.5453123);
            }

            // 2D Noise
            float noise(vec2 st) {
                vec2 i = floor(st);
                vec2 f = fract(st);
                
                float a = random(i);
                float b = random(i + vec2(1.0, 0.0));
                float c = random(i + vec2(0.0, 1.0));
                float d = random(i + vec2(1.0, 1.0));

                vec2 u = f * f * (3.0 - 2.0 * f);
                return mix(a, b, u.x) + (c - a)* u.y * (1.0 - u.x) + (d - b) * u.x * u.y;
            }

            // Wave function with turbulence
            float wave(vec2 st, float time, float freq, float amp, float turbAmt) {
                // Add turbulence
                float turb = noise(st * 2.0 + time * 0.5) * turbAmt;
                
                // Combine different wave frequencies
                float wave1 = sin(st.x * freq + time + turb) * amp;
                float wave2 = sin(st.y * freq * 1.3 + time * 1.1 + turb) * amp * 0.8;
                float wave3 = sin((st.x + st.y) * freq * 0.8 + time * 0.9 + turb) * amp * 0.6;
                
                return wave1 + wave2 + wave3;
            }

            // Ripple function
            float ripple(vec2 st, float time) {
                vec2 center = vec2(0.5);
                float dist = distance(st, center);
                float rippleWave = sin(dist * u_frequency * 3.0 - time * 2.0) * u_ripples * 0.02;
                return rippleWave * smoothstep(1.0, 0.0, dist * 2.0);
            }

            void main() {
                vec2 uv = v_texcoord;
                float aspect = u_resolution.x / u_resolution.y;
                vec2 st = uv;
                st.x *= aspect;
                
                float time = u_time * u_speed;
                float angle = u_direction * PI / 180.0;
                
                // Rotate coordinates
                st = (st - 0.5) * rotate2d(angle) + 0.5;
                
                // Initialize displacement
                vec2 displacement = vec2(0.0);
                
                // Add multiple wave layers
                for(float i = 0.0; i < 5.0; i++) {
                    if(i >= u_waveCount) break;
                    
                    float fi = i / u_waveCount;
                    float amp = u_amplitude * (1.0 - fi * 0.2);
                    float freq = u_frequency * (1.0 + fi * 0.3);
                    float phase = time * (1.0 - fi * 0.1);
                    
                    displacement += vec2(
                        wave(st + fi, phase, freq, amp, u_turbulence),
                        wave(st + fi + 1.234, phase * 1.1, freq * 1.2, amp * 0.8, u_turbulence)
                    );
                }
                
                // Add ripple effect
                float rippleEffect = ripple(uv, time);
                displacement += vec2(rippleEffect);
                
                // Sample with chromatic aberration
                float r = texture2D(u_image, uv + displacement * (1.0 + u_chromatic)).r;
                float g = texture2D(u_image, uv + displacement).g;
                float b = texture2D(u_image, uv + displacement * (1.0 - u_chromatic)).b;
                
                // Add refraction highlights
                float refraction = length(displacement) * u_refraction * 10.0;
                vec3 color = vec3(r, g, b) + refraction;
                
                gl_FragColor = vec4(color, 1.0);
            }
        `;

        await super.init(vertexSource, fragmentSource);
    }

    setupUniforms() {
        super.setupUniforms();
        
        this.addUniform('u_amplitude', { type: '1f' });
        this.addUniform('u_frequency', { type: '1f' });
        this.addUniform('u_speed', { type: '1f' });
        this.addUniform('u_direction', { type: '1f' });
        this.addUniform('u_turbulence', { type: '1f' });
        this.addUniform('u_ripples', { type: '1f' });
        this.addUniform('u_refraction', { type: '1f' });
        this.addUniform('u_chromatic', { type: '1f' });
        this.addUniform('u_waveCount', { type: '1f' });
    }

    updateUniforms(uniforms) {
        super.updateUniforms(uniforms);
        
        // Get base values
        let amplitude = this.params.amplitude;
        let frequency = this.params.frequency;
        let speed = this.params.speed;
        let direction = this.params.direction;
        let turbulence = this.params.turbulence;
        let ripples = this.params.ripples;
        
        // Add pulsing animation if enabled
        if (this.params.animate && uniforms.u_time) {
            const time = uniforms.u_time.value;
            const pulseTime = time * this.params.pulseSpeed;
            const amount = this.params.pulseAmount;
            
            // Create different pulse patterns
            const pulses = {
                slow: Math.sin(pulseTime * 0.3),
                medium: Math.sin(pulseTime * 0.7),
                fast: Math.sin(pulseTime * 1.1)
            };
            
            // Modulate parameters
            amplitude *= 1.0 + pulses.medium * amount;
            frequency *= 1.0 + pulses.slow * amount * 0.3;
            speed *= 1.0 + pulses.fast * amount * 0.2;
            direction += pulseTime * 10.0 * amount;
            turbulence *= 1.0 + pulses.medium * amount * 0.5;
            ripples *= 1.0 + pulses.fast * amount * 0.4;
        }
        
        Object.assign(uniforms, {
            u_amplitude: { value: amplitude, type: '1f' },
            u_frequency: { value: frequency, type: '1f' },
            u_speed: { value: speed, type: '1f' },
            u_direction: { value: direction, type: '1f' },
            u_turbulence: { value: turbulence, type: '1f' },
            u_ripples: { value: ripples, type: '1f' },
            u_refraction: { value: this.params.refraction, type: '1f' },
            u_chromatic: { value: this.params.chromatic, type: '1f' },
            u_waveCount: { value: this.params.waveCount, type: '1f' }
        });

        if (!this.params.animate) {
            uniforms.u_time.value = 0;
        }
    }

    getUIConfig() {
        return {
            amplitude: {
                min: 0,
                max: 0.1,
                step: 0.001,
                label: 'Wave Height'
            },
            frequency: {
                min: 1,
                max: 20,
                step: 0.5,
                label: 'Wave Frequency'
            },
            speed: {
                min: 0,
                max: 3,
                step: 0.1,
                label: 'Wave Speed'
            },
            direction: {
                min: 0,
                max: 360,
                step: 1,
                label: 'Wave Direction'
            },
            turbulence: {
                min: 0,
                max: 1,
                step: 0.01,
                label: 'Turbulence'
            },
            ripples: {
                min: 0,
                max: 1,
                step: 0.01,
                label: 'Ripples'
            },
            refraction: {
                min: 0,
                max: 1,
                step: 0.01,
                label: 'Refraction'
            },
            chromatic: {
                min: 0,
                max: 1,
                step: 0.01,
                label: 'Chromatic'
            },
            waveCount: {
                min: 1,
                max: 5,
                step: 1,
                label: 'Wave Layers'
            },
            animate: {
                label: 'Animate'
            },
            pulseSpeed: {
                min: 0.1,
                max: 3.0,
                step: 0.1,
                label: 'Pulse Speed'
            },
            pulseAmount: {
                min: 0,
                max: 1,
                step: 0.01,
                label: 'Pulse Amount'
            }
        };
    }
} 